import React, { useState, useEffect } from 'react'
import { getAccessToken } from '../utils/tokenStorage'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Grid,
  Avatar,
  Tooltip,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { institutionService } from '../services/api'
import { 
  InstitutionFilters, 
  InstitutionWithDetails
} from '../types/api'
import toast from 'react-hot-toast'
import { InstitutionForm } from '../components/institutions/InstitutionForm'

// Componenta principală pentru lista instituțiilor
const InstitutionsListPage: React.FC = () => {
  // State pentru date
  const [institutions, setInstitutions] = useState<InstitutionWithDetails[]>([])
  const [totalInstitutions, setTotalInstitutions] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [filters, setFilters] = useState<InstitutionFilters>({})
  const [searchTerm, setSearchTerm] = useState('')

  // State pentru dialog-uri și pagini
  const [institutionDialogOpen, setInstitutionDialogOpen] = useState(false)
  const [selectedInstitution, setSelectedInstitution] = useState<InstitutionWithDetails | undefined>()
  
  // State pentru Add Member dialog
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false)
  const [selectedInstitutionForMember, setSelectedInstitutionForMember] = useState<InstitutionWithDetails | undefined>()
  const [availableUsers, setAvailableUsers] = useState<any[]>([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedRole, setSelectedRole] = useState('medic')
  const [isCoordinator, setIsCoordinator] = useState(false) // ✨ Checkbox pentru coordonator
  const [addingMember, setAddingMember] = useState(false)
  const [institutionMembers, setInstitutionMembers] = useState<any[]>([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [institutionCoordinators, setInstitutionCoordinators] = useState<string[]>([]) // ✨ Lista user_ids coordonatori

  // Încarcă instituțiile
  const loadInstitutions = async () => {
    try {
      setLoading(true)
      // console.log('Încărcare instituții...')
      
      const response = await institutionService.getInstitutions({
        ...filters,
        q: searchTerm,
        skip: page * rowsPerPage,
        limit: rowsPerPage
      })
      
      // console.log('Răspuns API instituții:', response)
      setInstitutions(response.institutions)
      setTotalInstitutions(response.total)
    } catch (error: any) {
      console.error('Eroare la încărcarea instituțiilor:', error)
      console.error('Detalii eroare:', error.response?.data)
      toast.error('Eroare la încărcarea instituțiilor')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadInstitutions()
  }, [page, rowsPerPage, filters, searchTerm])

  // Funcții pentru Add Member
  const handleAddMember = async (institution: InstitutionWithDetails) => {
    try {
      setSelectedInstitutionForMember(institution)
      await loadAvailableUsers(institution.id)
      await loadInstitutionMembers(institution.id)
      setAddMemberDialogOpen(true)
    } catch (error) {
      console.error('Error opening add member dialog:', error)
      toast.error('Eroare la deschiderea dialogului')
    }
  }

  const loadInstitutionMembers = async (institutionId: string) => {
    try {
      setLoadingMembers(true)
      const members = await institutionService.getInstitutionMembers(institutionId)
      setInstitutionMembers(members || [])
      
      // ✨ Încarcă și lista de coordonatori pentru instituție
      await loadInstitutionCoordinators(institutionId, members || [])
    } catch (error) {
      console.error('Error loading institution members:', error)
      toast.error('Eroare la încărcarea membrilor')
    } finally {
      setLoadingMembers(false)
    }
  }

  // ✨ Încarcă coordonatorii pentru instituție (verifică fiecare membru medic)
  const loadInstitutionCoordinators = async (institutionId: string, members: any[]) => {
    try {
      const coordinatorIds: string[] = []
      
      // Pentru fiecare membru medic, verifică dacă e coordonator
      const medicMembers = members.filter(m => m.role === 'medic')
      
      for (const member of medicMembers) {
        try {
          const coordInstitutions = await institutionService.getUserCoordinatedInstitutions(member.user_id)
          const isCoord = coordInstitutions.some((inst: any) => inst.id === institutionId)
          if (isCoord) {
            coordinatorIds.push(member.user_id)
          }
        } catch (e) {
          // Ignore individual errors
        }
      }
      
      setInstitutionCoordinators(coordinatorIds)
    } catch (error) {
      console.error('Error loading coordinators:', error)
      setInstitutionCoordinators([])
    }
  }

  const loadAvailableUsers = async (institutionId: string) => {
    try {
      // Încarcă TOȚI utilizatorii (limit mare pentru a-i avea pe toți)
      const response = await fetch('/api/v1/users/?limit=500', {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const data = await response.json()
      // API-ul poate returna direct array sau { users: [...] }
      const allUsers = Array.isArray(data) ? data : (data.users || [])
      
      // Încarcă membrii existenți ai instituției
      const members = await institutionService.getInstitutionMembers(institutionId)
      const memberUserIds = members.map((m: any) => m.user_id)
      
      // Filtrează utilizatorii care nu sunt deja membri
      const available = allUsers.filter((user: any) => !memberUserIds.includes(user.id))
      setAvailableUsers(available)
    } catch (error) {
      console.error('Error loading users:', error)
      toast.error('Eroare la încărcarea utilizatorilor')
    }
  }

  const handleSaveNewMember = async () => {
    if (!selectedUserId || !selectedRole || !selectedInstitutionForMember) {
      toast.error('Selectează utilizatorul și rolul')
      return
    }

    try {
      setAddingMember(true)
      await institutionService.addInstitutionMember(selectedInstitutionForMember.id, selectedUserId, selectedRole)
      
      // ✨ Dacă e medic și e bifat coordonator, adaugă și ca coordonator
      if (selectedRole === 'medic' && isCoordinator) {
        try {
          await institutionService.addInstitutionCoordinator(selectedInstitutionForMember.id, selectedUserId)
          toast.success('Membru și coordonator adăugat cu succes')
        } catch (coordError) {
          console.error('Error adding coordinator:', coordError)
          toast.success('Membru adăugat, dar eroare la setare coordonator')
        }
      } else {
        toast.success('Membru adăugat cu succes')
      }
      
      // Reset doar form-ul, NU închide dialog-ul
      setSelectedUserId('')
      setSelectedRole('medic')
      setIsCoordinator(false)
      
      // Reîncarcă membrii și utilizatorii disponibili
      await loadInstitutionMembers(selectedInstitutionForMember.id)
      await loadAvailableUsers(selectedInstitutionForMember.id)
      
      // Reîncarcă instituțiile pentru a actualiza numărul de membri
      loadInstitutions()
    } catch (error) {
      console.error('Error adding member:', error)
      toast.error('Eroare la adăugarea membrului')
    } finally {
      setAddingMember(false)
    }
  }

  const handleRemoveMember = async (userId: string) => {
    if (!selectedInstitutionForMember) return

    try {
      await institutionService.removeInstitutionMember(selectedInstitutionForMember.id, userId)
      toast.success('Membru eliminat cu succes')
      
      // Reîncarcă membrii și utilizatorii disponibili
      await loadInstitutionMembers(selectedInstitutionForMember.id)
      await loadAvailableUsers(selectedInstitutionForMember.id)
      
      // Reîncarcă instituțiile pentru a actualiza numărul de membri
      loadInstitutions()
    } catch (error) {
      console.error('Error removing member:', error)
      toast.error('Eroare la eliminarea membrului')
    }
  }



  // Gestionează ștergerea instituției
  const handleDeleteInstitution = async (institutionId: string) => {
    if (!confirm('Sigur doriți să ștergeți această instituție?')) return

    try {
      await institutionService.deleteInstitution(institutionId)
      toast.success('Instituție ștearsă cu succes')
      loadInstitutions()
    } catch (error: any) {
      console.error('Eroare la ștergere:', error)
      toast.error('Eroare la ștergere')
    }
  }

  // Gestionează activarea/dezactivarea instituției
  const handleToggleInstitutionStatus = async (institutionId: string, active: boolean) => {
    try {
      await institutionService.updateInstitutionStatus(institutionId, active)
      toast.success(`Instituție ${active ? 'activată' : 'dezactivată'} cu succes`)
      loadInstitutions()
    } catch (error: any) {
      console.error('Eroare la schimbarea statusului:', error)
      toast.error('Eroare la schimbarea statusului')
    }
  }

  // Gestionează schimbarea paginii
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Gestionează schimbarea numărului de rânduri per pagină
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Filtre pentru status
  const statusFilters = [
    { value: '', label: 'Toate' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ]

  const getStatusColor = (active: boolean) => {
    return active ? 'success' : 'error'
  }

  const getStatusLabel = (active: boolean) => {
    return active ? 'Activă' : 'Inactivă'
  }


  // Styling compact pentru input-uri
  const inputSx = { 
    '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, 
    '& .MuiInputLabel-root': { fontSize: '0.7rem' }
  }

  return (
    <Box sx={{ p: 1.5 }}>

      {/* Filtre și căutare - compact inline */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Căutare instituții"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />
            }}
            placeholder="Caută după nume..."
            sx={{ minWidth: 200, ...inputSx }}
          />

          <FormControl size="small" sx={{ minWidth: 100, ...inputSx }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.active === undefined ? '' : filters.active.toString()}
              onChange={(e) => setFilters({ 
                ...filters, 
                active: e.target.value === '' ? undefined : e.target.value === 'true' 
              })}
              label="Status"
            >
              {statusFilters.map((filter) => (
                <MenuItem key={filter.value} value={filter.value} sx={{ fontSize: '0.75rem' }}>
                  {filter.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={loadInstitutions}
            disabled={loading}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}
          >
            Actualizează
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setSelectedInstitution(undefined)
              setInstitutionDialogOpen(true)
            }}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5, ml: 'auto' }}
          >
            Instituție Nouă
          </Button>
        </Box>
      </Paper>

      {/* Tabel cu instituții - compact cu dark header */}
      <Paper sx={{
        '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
        '& .MuiChip-root': { height: 20, fontSize: '0.72rem' },
        '& .MuiTableCell-root': { py: 0.5, px: 1 },
        '& .MuiTableBody-root .MuiIconButton-root': { p: '3px' },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': {
                  color: 'common.white',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  py: 1,
                  px: 1.5,
                  whiteSpace: 'nowrap'
                }
              }}>
                <TableCell>Instituție</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>PACS asociat</TableCell>
                <TableCell>Membri</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !institutions || institutions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nu s-au găsit instituții
                  </TableCell>
                </TableRow>
              ) : (
                institutions.map((institution) => (
                  <TableRow key={institution.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Box>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                            {institution.name}
                          </Typography>
                          {institution.external_id && (
                            <Typography sx={{ fontSize: '0.7rem' }} color="text.secondary">
                              {institution.external_id}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={institution.institution_type || 'Spital'} 
                        size="small" 
                        color="default" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={institution.tenant_name || 'Necunoscut'} 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${institution.pacs_count || 0} PACS`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={institution.member_count} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(institution.active)}
                        color={getStatusColor(institution.active)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Adaugă membru">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleAddMember(institution)}
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editează">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => {
                              setSelectedInstitution(institution)
                              setInstitutionDialogOpen(true)
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Șterge">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteInstitution(institution.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={institution.active ? 'Dezactivează' : 'Activează'}>
                          <Switch 
                            checked={institution.active} 
                            size="small"
                            color={institution.active ? 'success' : 'default'}
                            onChange={() => handleToggleInstitutionStatus(institution.id, !institution.active)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginare */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalInstitutions}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rânduri per pagină:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} din ${count !== -1 ? count : `mai mult de ${to}`}`
          }
        />
      </Paper>

      {/* Dialog pentru creare/editare instituție */}
      <InstitutionForm
        open={institutionDialogOpen}
        onClose={() => {
          setInstitutionDialogOpen(false)
          setSelectedInstitution(undefined)
        }}
        institution={selectedInstitution}
        onSave={() => {
          loadInstitutions()
        }}
      />

      {/* Dialog pentru adăugarea de membri - compact styling */}
      <Dialog 
        open={addMemberDialogOpen} 
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            setAddMemberDialogOpen(false)
            setSelectedInstitutionForMember(undefined)
            setSelectedUserId('')
            setSelectedRole('medic')
            setIsCoordinator(false)
            setInstitutionMembers([])
            setInstitutionCoordinators([])
          }
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            '& .MuiInputLabel-root': { fontSize: '0.8rem' },
            '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
            '& .MuiMenuItem-root': { fontSize: '0.8rem' },
          }
        }}
      >
        <DialogTitle sx={{ 
          py: 1.5, 
          px: 2,
          fontSize: '1rem',
          fontWeight: 600,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          Membri - {selectedInstitutionForMember?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {/* Adăugare membru nou - inline compact */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ 
              fontWeight: 600, 
              color: 'primary.main',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1,
              display: 'block'
            }}>
              Adaugă Membru
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>Utilizator</InputLabel>
                <Select
                  value={selectedUserId}
                  label="Utilizator"
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  {availableUsers.map((user) => {
                    // Extrage prenume și nume din diverse structuri
                    const firstName = user.first_name || user.doctor_profile?.first_name || ''
                    const lastName = user.last_name || user.doctor_profile?.last_name || ''
                    const fullName = `${firstName} ${lastName}`.trim()
                    // Afișează doar nume prenume
                    return (
                      <MenuItem key={user.id} value={user.id}>
                        {fullName || `[${user.email}]`}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={selectedRole}
                  label="Rol"
                  onChange={(e) => {
                    setSelectedRole(e.target.value)
                    // Reset coordinator checkbox dacă nu e medic
                    if (e.target.value !== 'medic') {
                      setIsCoordinator(false)
                    }
                  }}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="medic">Medic</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="tehnician">Tehnician</MenuItem>
                </Select>
              </FormControl>
              
              {/* ✨ Checkbox Coordonator - vizibil doar pentru medici */}
              {selectedRole === 'medic' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCoordinator}
                      onChange={(e) => setIsCoordinator(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Coordonator"
                  sx={{ 
                    ml: 0.5,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.8rem',
                      color: isCoordinator ? 'primary.main' : 'text.secondary'
                    }
                  }}
                />
              )}
              
              <Button 
                variant="outlined"
                size="small"
                onClick={handleSaveNewMember}
                disabled={addingMember || !selectedUserId}
                startIcon={addingMember ? <CircularProgress size={14} /> : <PersonAddIcon sx={{ fontSize: 16 }} />}
                sx={{ height: 32, fontSize: '0.78rem', textTransform: 'none' }}
              >
                Adaugă
              </Button>
            </Box>
          </Box>

          {/* Lista membrilor existenți */}
          <Typography variant="caption" sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 1,
            display: 'block'
          }}>
            Membri ({institutionMembers.length})
          </Typography>
          {loadingMembers ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : institutionMembers.length === 0 ? (
            <Typography sx={{ fontSize: '0.82rem', p: 1.5, textAlign: 'center' }} color="text.secondary">
              Nu există membri asociați
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{
              '& .MuiTableCell-root': { py: 0.75, px: 1, fontSize: '0.82rem' }
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.78rem', fontWeight: 600 } }}>
                    <TableCell>Email</TableCell>
                    <TableCell>Nume</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell width={80}>Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {institutionMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Chip 
                            label={member.role}
                            size="small"
                            sx={{ height: 20, fontSize: '0.72rem' }}
                            color={
                              member.role === 'admin' ? 'error' : 
                              member.role === 'medic' ? 'primary' :
                              member.role === 'operator' ? 'warning' : 'default'
                            }
                          />
                          {/* ✨ Badge coordonator pentru medici */}
                          {member.role === 'medic' && institutionCoordinators.includes(member.user_id) && (
                            <Chip 
                              label="Coord"
                              size="small"
                              sx={{ height: 18, fontSize: '0.68rem' }}
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {/* ✨ Toggle coordonator pentru medici */}
                          {member.role === 'medic' && (
                            <Tooltip title={institutionCoordinators.includes(member.user_id) ? 'Elimină rol coordonator' : 'Setează ca coordonator'}>
                              <Checkbox
                                checked={institutionCoordinators.includes(member.user_id)}
                                onChange={async (e) => {
                                  try {
                                    if (e.target.checked) {
                                      await institutionService.addInstitutionCoordinator(selectedInstitutionForMember!.id, member.user_id)
                                      setInstitutionCoordinators([...institutionCoordinators, member.user_id])
                                      toast.success('Coordonator adăugat')
                                    } else {
                                      await institutionService.removeInstitutionCoordinator(selectedInstitutionForMember!.id, member.user_id)
                                      setInstitutionCoordinators(institutionCoordinators.filter(id => id !== member.user_id))
                                      toast.success('Rol coordonator eliminat')
                                    }
                                  } catch (error) {
                                    console.error('Error toggling coordinator:', error)
                                    toast.error('Eroare la modificare coordonator')
                                  }
                                }}
                                size="small"
                                sx={{ p: 0.25 }}
                              />
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(member.user_id)}
                            sx={{ p: 0.5 }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            variant="contained"
            size="small"
            onClick={() => {
              setAddMemberDialogOpen(false)
              setSelectedInstitutionForMember(undefined)
              setSelectedUserId('')
              setSelectedRole('medic')
              setIsCoordinator(false)
              setInstitutionMembers([])
              setInstitutionCoordinators([])
            }}
            sx={{ fontSize: '0.8rem' }}
          >
            Închide
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default InstitutionsListPage
